import { Link } from '@tanstack/react-router';
import type { InventoryItem } from '@siliconvault/shared';
import { useTranslation } from '../../i18n';
import { useAuth } from '../../hooks/useAuth';
import { FavoriteButton } from '../community/FavoriteButton';
import styles from './ItemCard.module.css';

interface ItemCardProps {
  item: InventoryItem;
  showOwner?: boolean;
  from?: string;
}

function conditionClass(condition: string): string {
  switch (condition) {
    case 'Mint':
    case 'Mint in Box':
      return styles.conditionMint;
    case 'Good':
    case 'Very Good':
      return styles.conditionGood;
    case 'Fair':
      return styles.conditionFair;
    case 'Poor':
      return styles.conditionPoor;
    case 'For Parts':
      return styles.conditionForParts;
    default:
      return styles.conditionGood;
  }
}

export function ItemCard({ item, showOwner, from }: ItemCardProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isOwnItem = item.createdBy === user?.userId;
  const showFavorite = showOwner && !isOwnItem;

  return (
    <Link
      to="/inventory/$itemId"
      params={{ itemId: item.id }}
      search={from ? { from } : { from: undefined }}
      className={styles.card}
      title={[item.platform, item.category, item.subcategory].filter(Boolean).join(' · ')}
    >
      <div className={styles.imageWrap}>
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            className={styles.image}
            loading="lazy"
          />
        ) : (
          <div className={styles.imagePlaceholder}>{t('itemCard.noImage')}</div>
        )}
      </div>
      <div className={styles.body}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.25rem' }}>
          <div className={styles.name}>{item.name}</div>
          {showFavorite && <FavoriteButton itemId={item.id} compact />}
        </div>
        {showOwner && item.ownerDisplayName && (
          <div className={styles.owner}>
            {item.ownerDisplayName}
            {item.ownerLocation && <span className={styles.ownerLocation}> · {item.ownerLocation}</span>}
          </div>
        )}
        <div className={styles.meta}>
          <span className={`${styles.tag} ${conditionClass(item.condition)}`}>
            {item.condition}
          </span>
          {item.listingStatus === 'for-trade' && (
            <span className={`${styles.tag} ${styles.conditionGood}`}>For Trade</span>
          )}
          {item.listingStatus === 'for-sale' && (
            <span className={`${styles.tag} ${styles.conditionMint}`}>
              {item.askingPrice ? `${item.listingCurrency} ${item.askingPrice}` : 'For Sale'}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
